import { LanguageSwitcher } from "#/components/i18n/LanguageSwitcher.js";
import type { TranslateFn } from "#/lib/i18n.js";
import {
  DEFAULT_LOCALE,
  LOCALE_URL_SEGMENTS,
  type LocaleCode,
  isLocaleCode,
} from "#/lib/i18n/locales.js";

export function getLocalizedHref(locale: string, href: string): string {
  const safeHref = href.startsWith("/") ? href : `/${href}`;
  if (locale === DEFAULT_LOCALE) return safeHref;
  const segment = locale.toLowerCase();
  if (!LOCALE_URL_SEGMENTS.has(segment)) return safeHref;
  if (safeHref === "/") return `/${segment}`;
  return `/${segment}${safeHref}`;
}

export function safeLocale(locale: string): LocaleCode {
  return isLocaleCode(locale) ? locale : DEFAULT_LOCALE;
}

export function BrandLogo({
  href,
  t,
  size = "md",
  centered = false,
}: {
  href: string;
  t: TranslateFn;
  size?: "sm" | "md" | "lg";
  centered?: boolean;
}) {
  const imageSize = size === "lg" ? 48 : size === "sm" ? 32 : 40;
  return (
    <a
      href={href}
      aria-label={t("chrome.brandAria")}
      className={[
        "inline-flex min-h-11 min-w-0 items-center gap-3 text-[color:var(--color-fg-default)] no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg-base)]",
        centered ? "justify-center" : "",
      ].join(" ")}
    >
      <img
        src="/branding/hantavir-us-mark.png"
        alt=""
        width={imageSize}
        height={imageSize}
        className="shrink-0"
        style={{ inlineSize: imageSize, blockSize: imageSize }}
      />
      <span
        className={[
          "truncate font-semibold leading-none tracking-normal",
          size === "lg" ? "font-serif text-2xl" : size === "sm" ? "text-base" : "text-lg",
        ].join(" ")}
      >
        hantavir.us
      </span>
    </a>
  );
}

export function ChromeNav({
  locale,
  t,
  compact = false,
}: {
  locale: string;
  t: TranslateFn;
  compact?: boolean;
}) {
  const navItems = [
    { href: getLocalizedHref(locale, "/subscribe"), label: t("chrome.nav.subscribe") },
    { href: getLocalizedHref(locale, "/download"), label: t("chrome.nav.download") },
  ];
  return (
    <nav
      aria-label={t("chrome.nav.label")}
      className="flex min-w-0 flex-wrap items-center gap-1 sm:justify-end sm:gap-2"
    >
      {navItems.map((item) => (
        <a
          key={item.href}
          href={item.href}
          className={[
            "inline-flex min-h-11 min-w-11 items-center justify-center rounded-md text-sm font-medium text-[color:var(--color-fg-muted)] no-underline transition-colors duration-150 hover:bg-[color:var(--color-bg-surface)] hover:text-[color:var(--color-fg-default)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent-info)]",
            compact ? "px-2" : "px-2 sm:px-3",
          ].join(" ")}
        >
          {item.label}
        </a>
      ))}
      <LanguageSwitcher currentLocale={safeLocale(locale)} variant="navbar" t={t} />
    </nav>
  );
}
